import { RequiredActionType, BaseActionConfig } from "../types/tour";
import { logger } from "../utils/logger";

export class TourActionDetection {
  private listeners: Map<string, () => void> = new Map();
  private observers: Map<string, MutationObserver> = new Map();
  private intervalChecks: Map<string, NodeJS.Timeout> = new Map();

  /**
   * Detecta quando uma ação específica foi realizada
   */
  public detectAction(
    actionType: RequiredActionType,
    config: BaseActionConfig,
    onActionCompleted: () => void,
    stepId: string,
  ): void {
    switch (actionType) {
      case "click":
        this.detectClickAction(config, onActionCompleted, stepId);
        break;
      case "open":
        this.detectOpenAction(config, onActionCompleted, stepId);
        break;
      case "inputFilled":
        this.detectInputFilledAction(config, onActionCompleted, stepId);
        break;
      case "navigation":
        this.detectNavigationAction(config, onActionCompleted, stepId);
        break;
      default:
        logger.warn(
          `[TourActionDetection] Tipo de ação não suportado: ${actionType}`,
        );
    }
  }

  /**
   * Detecta clique em elemento específico
   */
  private detectClickAction(
    config: BaseActionConfig,
    onActionCompleted: () => void,
    stepId: string,
  ): void {
    const element = document.querySelector(config.selector!);

    if (!element) {
      logger.warn(
        `[TourActionDetection] Elemento não encontrado para clique: ${config.selector}`,
      );
      return;
    }

    const clickHandler = (event: Event) => {
      if (config.preventDefault) {
        event.preventDefault();
      }

      onActionCompleted();
      this.cleanup(stepId);
    };

    element.addEventListener("click", clickHandler);
    this.listeners.set(stepId, () => {
      element.removeEventListener("click", clickHandler);
    });
  }

  /**
   * Detecta abertura de painel/modal/aba
   */
  private detectOpenAction(
    config: BaseActionConfig,
    onActionCompleted: () => void,
    stepId: string,
  ): void {
    const checkIfOpen = () => {
      const element = document.querySelector(config.selector!);

      if (!element) {
        return false;
      }

      // Verificar por atributo
      if (config.checkAttribute) {
        const attrValue = element.getAttribute(config.checkAttribute);
        return attrValue === "true" || attrValue === "expanded";
      }

      // Verificar por classe
      if (config.checkClass) {
        return element.classList.contains(config.checkClass);
      }

      // Verificar por função customizada
      if (config.checkState) {
        return config.checkState();
      }

      // Verificar se elemento está visível
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    // Verificação inicial
    if (checkIfOpen()) {
      onActionCompleted();
      return;
    }

    // Observer para mudanças no DOM
    const observer = new MutationObserver(() => {
      if (checkIfOpen()) {
        onActionCompleted();
        this.cleanup(stepId);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: config.checkAttribute
        ? [config.checkAttribute]
        : undefined,
    });

    this.observers.set(stepId, observer);

    // Fallback com verificação por intervalo
    const intervalCheck = setInterval(() => {
      if (checkIfOpen()) {
        onActionCompleted();
        this.cleanup(stepId);
      }
    }, 500);

    this.intervalChecks.set(stepId, intervalCheck);
  }

  /**
   * Detecta preenchimento de campo de input
   */
  private detectInputFilledAction(
    config: BaseActionConfig,
    onActionCompleted: () => void,
    stepId: string,
  ): void {
    const element = document.querySelector(config.selector!) as
      | HTMLInputElement
      | HTMLTextAreaElement;

    if (!element) {
      logger.warn(
        `[TourActionDetection] Campo de input não encontrado: ${config.selector}`,
      );
      return;
    }

    const validateInput = () => {
      const value = element.value.trim();

      // Verificar se é obrigatório e está vazio
      if (config.required && !value) {
        return false;
      }

      // Verificar comprimento mínimo
      if (config.minLength && value.length < config.minLength) {
        return false;
      }

      // Verificar regex
      if (config.regex && !config.regex.test(value)) {
        return false;
      }

      return value.length > 0; // Pelo menos algum conteúdo
    };

    const inputHandler = () => {
      if (validateInput()) {
        onActionCompleted();
        this.cleanup(stepId);
      }
    };

    // Verificação inicial
    if (validateInput()) {
      onActionCompleted();
      return;
    }

    // Listeners para diferentes eventos de input
    const events = ["input", "change", "keyup", "paste"];
    events.forEach((eventType) => {
      element.addEventListener(eventType, inputHandler);
    });

    this.listeners.set(stepId, () => {
      events.forEach((eventType) => {
        element.removeEventListener(eventType, inputHandler);
      });
    });
  }

  /**
   * Detecta navegação para URL/rota específica
   */
  private detectNavigationAction(
    config: BaseActionConfig,
    onActionCompleted: () => void,
    stepId: string,
  ): void {
    const checkNavigation = () => {
      // Verificar URL
      if (config.url) {
        const currentUrl = window.location.href;
        if (currentUrl.includes(config.url)) {
          return true;
        }
      }

      // Verificar rota (pathname)
      if (config.route) {
        const currentRoute = window.location.pathname;
        if (
          currentRoute === config.route ||
          currentRoute.includes(config.route)
        ) {
          return true;
        }
      }

      // Verificar aba ativa (por ID ou classe)
      if (config.tabId) {
        const activeTab = document.querySelector(
          `${config.tabId}.active, ${config.tabId}[aria-selected="true"]`,
        );
        if (activeTab) {
          return true;
        }
      }

      return false;
    };

    // Verificação inicial
    if (checkNavigation()) {
      onActionCompleted();
      return;
    }

    // Listener para mudanças na URL
    const navigationHandler = () => {
      if (checkNavigation()) {
        onActionCompleted();
        this.cleanup(stepId);
      }
    };

    // Eventos de navegação
    window.addEventListener("popstate", navigationHandler);
    window.addEventListener("pushstate", navigationHandler);
    window.addEventListener("replacestate", navigationHandler);

    // Observer para mudanças em abas
    if (config.tabId) {
      const observer = new MutationObserver(navigationHandler);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "aria-selected"],
      });
      this.observers.set(stepId, observer);
    }

    this.listeners.set(stepId, () => {
      window.removeEventListener("popstate", navigationHandler);
      window.removeEventListener("pushstate", navigationHandler);
      window.removeEventListener("replacestate", navigationHandler);
    });

    // Verificação por intervalo como fallback
    const intervalCheck = setInterval(() => {
      if (checkNavigation()) {
        onActionCompleted();
        this.cleanup(stepId);
      }
    }, 1000);

    this.intervalChecks.set(stepId, intervalCheck);
  }

  /**
   * Remove todos os listeners e observers para um step específico
   */
  public cleanup(stepId: string): void {
    // Remover event listeners
    const listener = this.listeners.get(stepId);
    if (listener) {
      listener();
      this.listeners.delete(stepId);
    }

    // Remover observers
    const observer = this.observers.get(stepId);
    if (observer) {
      observer.disconnect();
      this.observers.delete(stepId);
    }

    // Remover interval checks
    const intervalCheck = this.intervalChecks.get(stepId);
    if (intervalCheck) {
      clearInterval(intervalCheck);
      this.intervalChecks.delete(stepId);
    }
  }

  /**
   * Remove todos os listeners e observers
   */
  public cleanupAll(): void {
    // Cleanup de todos os listeners
    this.listeners.forEach((listener) => {
      listener();
    });
    this.listeners.clear();

    // Cleanup de todos os observers
    this.observers.forEach((observer) => {
      observer.disconnect();
    });
    this.observers.clear();

    // Cleanup de todos os interval checks
    this.intervalChecks.forEach((intervalCheck) => {
      clearInterval(intervalCheck);
    });
    this.intervalChecks.clear();
  }

  /**
   * Verifica se um elemento existe no DOM
   */
  public static elementExists(selector: string): boolean {
    return document.querySelector(selector) !== null;
  }

  /**
   * Aguarda um elemento aparecer no DOM
   */
  public static waitForElement(
    selector: string,
    timeout: number = 5000,
  ): Promise<Element> {
    return new Promise((resolve, reject) => {
      const element = document.querySelector(selector);

      if (element) {
        resolve(element);
        return;
      }

      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (element) {
          observer.disconnect();
          resolve(element);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      setTimeout(() => {
        observer.disconnect();
        reject(
          new Error(`Elemento não encontrado após ${timeout}ms: ${selector}`),
        );
      }, timeout);
    });
  }
}
